import React from 'react';
import { BaseModal, Button, FeatureEmptyGuideCard } from 'src/components';
import useModal from 'src/hooks/useModal';
import { config } from 'src/utils/config';

/**
 * How-to for the FileSpin Adobe plugin, opened from the asset modal's Edit menu
 * for users whose edit is beyond what the in-app editors do.
 *
 * Every claim here is taken from the plugin repo (README/RELEASE/TESTING) — the
 * host apps and their menu paths, the device-code sign-in, the three save
 * destinations. Nothing about Illustrator or After Effects: neither has a public
 * UXP API, so the plugin does not support them.
 *
 * Kept to the steps on purpose. The irreversible-overwrite warning is not
 * repeated here — the panel's own confirmation dialog states it at the moment it
 * matters, with the count of conversions that will be regenerated, which is a
 * better place for it than a page the user reads before installing anything.
 */

const HOSTS = 'Photoshop, InDesign and Premiere Pro';

type Props = {
  /** Drives the video-only note about Premiere's export step. */
  contentType?: string;
};

export const AdobeConnectorDialog: React.FC<Props> = ({ contentType }) => {
  const { closeModal } = useModal();
  const isVideo = (contentType || '').split('/')[0] === 'video';

  const steps = [
    {
      body: (
        <>
          <strong>Install the plugin.</strong> In the Creative Cloud app, open{' '}
          <em>Marketplace → Plugins</em> (or <em>Plugins → Browse</em> inside Photoshop), search
          for <strong>FileSpin</strong> and click <strong>Get</strong>. Each Adobe app is a
          separate install.
        </>
      ),
    },
    {
      body: (
        <>
          <strong>Open the panel.</strong> In Photoshop and InDesign it is under{' '}
          <em>Plugins ▸ FileSpin ▸ Media Connector</em>. In Premiere Pro it is{' '}
          <em>Window → UXP Plugins → FileSpin</em>.
        </>
      ),
    },
    {
      body: (
        <>
          <strong>Sign in.</strong> Click <em>Sign in to FileSpin</em> — the panel shows a short
          code and opens your browser with it already filled in. Approve it there and the panel
          signs in. Each Adobe app signs in separately.
        </>
      ),
    },
    {
      body: (
        <>
          <strong>Open an asset, edit, save back.</strong> Double-click an asset in the panel to
          load it{' '}
          {isVideo
            ? 'into your Premiere project'
            : 'into your document (Photoshop places it as a Smart Object; InDesign places it into the selected frame)'}
          . When you are done, save it back as a new asset, a new format alongside the original
          (e.g. <code>photoshop_edit</code>), or an update to the asset itself.
        </>
      ),
    },
  ];

  return (
    <BaseModal containerStyle={{ maxWidth: '640px', width: '90%' }}>
      <FeatureEmptyGuideCard
        title="Edit in Adobe apps"
        info={
          <>
            <p>
              For retouching, layout and colour work beyond the in-app editors, the FileSpin
              plugin for {HOSTS} opens your assets straight from FileSpin and saves your work
              back — no downloading, renaming or re-uploading.
            </p>
            {isVideo && (
              <p>
                In Premiere Pro the panel imports the clip into your project. Exporting stays
                yours: render with <em>File → Export → Media</em> using your own settings, then
                pick that file in the panel to save it back.
              </p>
            )}
          </>
        }
        steps={steps}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <Button mode="tertiary" size="small" onClick={closeModal}>
          Close
        </Button>
        <a href={config.adobePluginUrl} target="_blank" rel="noopener noreferrer">
          <Button mode="primary" size="small" datatest="adobe-plugin-cta">
            Get the plugin
          </Button>
        </a>
      </div>
    </BaseModal>
  );
};

export default AdobeConnectorDialog;
